import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { debounce } from "lodash";
import {
  CRUDLayout,
  InputSearch,
  Alert,
  Table,
  THead,
  TBody,
  ThFixed,
  TdFixed,
  Tr,
  Th,
  Td,
  ReadButton,
  UpdateButton,
  DataStatus,
  Pagination,
  ActionButton,
  CreateButton,
  BackButton,
} from "components";
import {
  DateConvert,
  DecimalConvert,
  RupiahConvert,
  TableNumber,
} from "utilities";
import { PermintaanProduksiApi } from "api";
import { IoAddOutline } from "react-icons/io5";
import { Button, ButtonGroup } from "react-bootstrap";

export const PermintaanProduksiProduksiList = ({ setNavbarTitle }) => {
  const history = useHistory();
  const [data, setData] = useState([]);
  const [search, setSearch] = useState(undefined);
  const [fetchingStatus, setFetchingStatus] = useState({
    loading: true,
    error: true,
  });
  const [alert, setAlert] = useState({
    show: false,
    variant: "primary",
    text: "",
  });
  const [pagination, setPagination] = useState({
    page: 1,
    dataLength: 10,
    totalPage: 1,
    dataCount: 0,
  });

  const getData = () => {
    setData([]);
    setFetchingStatus({
      loading: true,
      error: true,
    });

    PermintaanProduksiApi.getProduksi({
      page: pagination.page,
      per_page: pagination.dataLength,
      q: search,
    })
      .then((res) => {
        setData(res?.data?.data ?? []);
        setPagination({
          ...pagination,
          dataCount: res?.data?.data_count,
          totalPage: res?.data?.total_page,
        });
        setFetchingStatus({
          loading: false,
          error: false,
        });
      })
      .catch(() => {
        setData([]);
        setFetchingStatus({
          loading: false,
          error: true,
        });
      });
  };

  const searchHandler = (e) => {
    setSearch(e.target.value);
  };

  useEffect(() => {
    setNavbarTitle("Permintaan Produksi");
    setAlert({
      show: !!history?.location?.state?.alert?.show,
      variant: history?.location?.state?.alert?.variant,
      text: history?.location?.state?.alert?.text,
    });
  }, []);

  useEffect(() => {
    getData();
  }, [search, pagination.page, pagination.dataLength]);

  return (
    <CRUDLayout>
      {/* Head */}
      <CRUDLayout.Head>
        <CRUDLayout.HeadSearchSection>
          <div className="d-flex mb-0">
            <InputSearch onChange={debounce(searchHandler, 800)} />
          </div>
        </CRUDLayout.HeadSearchSection>
        <CRUDLayout.HeadButtonSection>
          <BackButton onClick={() => history.goBack()} />
        </CRUDLayout.HeadButtonSection>
      </CRUDLayout.Head>

      {/* Alert */}
      <Alert show={alert.show} variant={alert.variant} text={alert.text} />

      {/* Content */}
      {fetchingStatus.loading || fetchingStatus.error ? (
        <DataStatus
          loading={fetchingStatus.loading}
          text={fetchingStatus.loading ? "Memuat . . ." : "Data gagal dimuat"}
        />
      ) : (
        <>
          <div className="p-1">
            <b>List Produksi Yang Siap Dibuatkan Permintaan Produksi</b>
          </div>
          <Table>
            <THead>
              <Tr>
                <ThFixed>No.</ThFixed>
                <Th style={{ width: 150 }}>Informasi Produksi</Th>
                <Th style={{ width: 150 }}>Informasi Sales Order</Th>
                <Th style={{ minWidth: 200 }}>Customer</Th>
                <Th style={{ minWidth: 300 }}>Item Produksi</Th>
                <Th style={{ width: 150 }}>Qty. Sales Order</Th>
                <Th style={{ width: 150 }}>Qty. Produksi</Th>
                <Th>Status Permintaan</Th>
                <ThFixed>Aksi</ThFixed>
              </Tr>
            </THead>
            <TBody>
              {data && data?.length > 0 ? (
                data.map((e, i) => (
                  <Tr key={i}>
                    <TdFixed>
                      {TableNumber(pagination.page, pagination.dataLength, i)}
                    </TdFixed>
                    <Td>
                      <span className="d-block">
                        {e?.tgl_jobmix
                          ? DateConvert(new Date(e.tgl_jobmix)).defaultDMY
                          : "-"}
                      </span>
                      <span className="d-block">{e?.no_jobmix ?? "-"}</span>
                    </Td>
                    <Td>
                      <span className="d-block">
                        {e?.tgl_sales_order
                          ? DateConvert(new Date(e.tgl_sales_order)).defaultDMY
                          : "-"}
                      </span>
                      <span className="d-block">
                        {e?.no_sales_order ?? "-"}
                      </span>
                    </Td>
                    <Td>{e?.nama_customer ?? "-"}</Td>
                    <Td>{e?.nama_item ?? "-"}</Td>
                    <Td>
                      {e?.qty_item
                        ? DecimalConvert(e.qty_item).getWithComa
                        : "-"}{" "}
                      {e?.kode_satuan ?? ""}
                    </Td>
                    <Td>
                      {e?.qty_jobmix
                        ? DecimalConvert(e.qty_jobmix).getWithComa
                        : "-"}{" "}
                      {e?.kode_satuan ?? ""}
                    </Td>
                    <Td>
                      <Button
                        variant={
                          e.status_permintaan === "Sudah Diminta"
                            ? "outline-success"
                            : "outline-danger"
                        }
                        size="sm"
                        className="text-nowrap">
                        {e.status_permintaan}
                      </Button>
                    </Td>
                    <TdFixed>
                      <div className="d-flex">
                        <ReadButton
                          size="sm"
                          className="bg-info mr-1"
                          onClick={() =>
                            history.push(
                              "/transaksi/job-mix/detail/" + e?.id_jobmix
                            )
                          }
                        />
                        <ActionButton
                          size="sm"
                          text={<IoAddOutline />}
                          onClick={() =>
                            history.push(
                              "/transaksi/permintaan-produksi/tambah/" +
                                e?.id_jobmix
                            )
                          }
                        />
                      </div>
                    </TdFixed>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={11}>
                    <DataStatus text="Tidak ada data" />
                  </Td>
                </Tr>
              )}
            </TBody>
          </Table>
          <Pagination
            dataLength={pagination?.dataLength}
            dataNumber={
              pagination?.page * pagination?.dataLength -
              pagination?.dataLength +
              1
            }
            dataPage={
              pagination?.dataCount < pagination?.dataLength
                ? pagination?.dataCount
                : pagination?.page * pagination?.dataLength
            }
            dataCount={pagination?.dataCount}
            currentPage={pagination?.page}
            totalPage={pagination?.totalPage}
            onPaginationChange={({ selected }) =>
              setPagination({
                ...pagination,
                page: selected + 1,
              })
            }
            onDataLengthChange={(e) =>
              setPagination({
                ...pagination,
                page: 1,
                dataLength: e.target.value,
              })
            }
          />
        </>
      )}
    </CRUDLayout>
  );
};
